import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Sparkles, ArrowRight, Leaf, Droplet, ZoomIn } from 'lucide-react';
import Header from './Header';
import Footer from './Footer';
import LoadingSpinner from './LoadingSpinner';

const Gallery = () => {
  const [liquors, setLiquors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeFilter, setActiveFilter] = useState('Tutti');
  const [zoomedLiquor, setZoomedLiquor] = useState(null);

  // Gallery di liquori realizzati (showcase/portfolio)
  const portfolioLiquors = [
    {
      id: 1,
      name: "Gin Botanico Premium",
      type: "Gin",
      image: "https://images.unsplash.com/photo-1514362545857-3bc16c4c7d1b?w=800&q=80",
      description: "Un gin artigianale con note di ginepro, cardamomo e agrumi. Perfetto per cocktail sofisticati.",
      botanicals: ["Ginepro", "Cardamomo", "Arancia", "Coriandolo"],
      client: "Ristorante Il Giardino",
      bottles: 144
    },
    {
      id: 2,
      name: "Amaro Mediterraneo",
      type: "Amaro",
      image: "https://images.unsplash.com/photo-1569529465841-dfecdab7503b?w=800&q=80",
      description: "Amaro dalle note intense di erbe mediterranee e radici amare. Digestivo perfetto.",
      botanicals: ["Genziana", "Rabarbaro", "Anice", "Liquirizia"],
      client: "Hotel La Terrazza",
      bottles: 96
    },
    {
      id: 3,
      name: "Bitter Arancio Rosso",
      type: "Bitter",
      image: "https://images.unsplash.com/photo-1560963805-6c64417e3413?w=800&q=80",
      description: "Bitter dal carattere deciso con infusione di arancia rossa siciliana e spezie.",
      botanicals: ["Arancia Rossa", "Chinotto", "Assenzio", "Zenzero"],
      client: "Bar Centrale",
      bottles: 192
    },
    {
      id: 4,
      name: "Gin Floreale Delicato",
      type: "Gin",
      image: "https://images.unsplash.com/photo-1551538827-9c037cb4f32a?w=800&q=80",
      description: "Gin con note delicate di fiori di sambuco, rosa e lavanda. Elegante e raffinato.",
      botanicals: ["Sambuco", "Rosa", "Lavanda", "Limone"],
      client: "Enoteca Le Rose",
      bottles: 72
    },
    {
      id: 5,
      name: "Amaro Speziato Intenso",
      type: "Amaro",
      image: "https://images.unsplash.com/photo-1615332579937-4e3a6c7c1e6d?w=800&q=80",
      description: "Amaro dalle note speziate con cannella, chiodi di garofano e arancia amara.",
      botanicals: ["Cannella", "Chiodi Garofano", "Arancia Amara", "Cardamomo"],
      client: "Ristorante Da Carlo",
      bottles: 120
    },
    {
      id: 6,
      name: "Bitter Agrumato",
      type: "Bitter",
      image: "https://images.unsplash.com/photo-1556679343-c7306c1976bc?w=800&q=80",
      description: "Bitter rinfrescante con mix di agrumi mediterranei e note erbacee.",
      botanicals: ["Pompelmo", "Limone", "Bergamotto", "Timo"],
      client: "Hotel Vista Mare",
      bottles: 288
    }
  ];

  useEffect(() => {
    const loadGallery = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/gallery/public`);
        
        if (response.ok) {
          const data = await response.json();
          setLiquors(data);
        } else {
          // Fallback to portfolio liquors if API fails
          setLiquors(portfolioLiquors);
        }
      } catch (error) {
        console.error('Error loading gallery:', error);
        // Fallback to portfolio liquors
        setLiquors(portfolioLiquors);
      } finally {
        setLoading(false);
      }
    };

    loadGallery();
  }, []);

  // Filter liquors by type
  const filteredLiquors = activeFilter === 'Tutti' 
    ? liquors 
    : liquors.filter(liquor => liquor.type === activeFilter);

  // Count liquors by type
  const liquorCounts = {
    Tutti: liquors.length,
    Gin: liquors.filter(l => l.type === 'Gin').length,
    Amaro: liquors.filter(l => l.type === 'Amaro').length,
    Bitter: liquors.filter(l => l.type === 'Bitter').length
  };

  const filterOptions = ['Tutti', 'Gin', 'Amaro', 'Bitter'];

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-purple-900 via-purple-800 to-purple-900 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto">
            <div className="inline-flex items-center bg-white/10 backdrop-blur-sm rounded-full px-6 py-2 text-sm font-medium mb-6">
              <Sparkles className="w-4 h-4 mr-2 text-amber-300" />
              Le Nostre Creazioni
            </div>
            <h1 className="text-4xl md:text-6xl font-black mb-6 leading-tight">
              Gallery dei Liquori
              <span className="block text-amber-300">Realizzati</span>
            </h1>
            <p className="text-xl text-white/90 leading-relaxed">
              Scopri i liquori artigianali che abbiamo creato per i nostri partner professionali. 
              Ogni bottiglia racconta una storia unica di sapori e tradizione.
            </p>
          </div>
        </div>
      </section>

      {/* Gallery Content with Sidebar */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col lg:flex-row gap-8">
            
            {/* Sidebar */}
            <aside className="lg:w-64 flex-shrink-0">
              <div className="bg-white rounded-lg shadow-lg p-6 sticky top-4">
                {/* Filters */}
                <div className="mb-6">
                  <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
                    <Sparkles className="w-5 h-5 mr-2 text-purple-600" />
                    Filtra per Tipo
                  </h3>
                  <div className="space-y-2">
                    {filterOptions.map((filter) => (
                      <button
                        key={filter}
                        onClick={() => setActiveFilter(filter)}
                        className={`w-full text-left px-4 py-3 rounded-lg font-medium transition-all duration-300 ${
                          activeFilter === filter
                            ? 'bg-gradient-to-r from-purple-600 to-purple-700 text-white shadow-md'
                            : 'bg-gray-50 text-gray-700 hover:bg-gray-100'
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <span>{filter}</span>
                          <span className={`text-xs px-2 py-0.5 rounded-full ${
                            activeFilter === filter
                              ? 'bg-white/20 text-white'
                              : 'bg-gray-200 text-gray-600'
                          }`}>
                            {liquorCounts[filter]}
                          </span>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Info Box */}
                <div className="border-t pt-6">
                  <h4 className="text-sm font-semibold text-gray-900 mb-3">
                    📊 Statistiche Gallery
                  </h4>
                  <div className="space-y-2 text-sm text-gray-600">
                    <p>
                      <span className="font-medium text-gray-900">{filteredLiquors.length}</span> {filteredLiquors.length === 1 ? 'liquore' : 'liquori'} {activeFilter !== 'Tutti' ? `di tipo ${activeFilter}` : 'in totale'}
                    </p>
                    <p className="text-xs text-gray-500 mt-4">
                      Ogni liquore è una creazione unica realizzata per i nostri partner professionali
                    </p>
                  </div>
                </div>

                {/* CTA */}
                <div className="border-t pt-6 mt-6">
                  <Link
                    to="/builder"
                    className="block w-full bg-gradient-to-r from-amber-500 to-amber-600 text-white text-center px-4 py-3 rounded-lg font-semibold hover:from-amber-600 hover:to-amber-700 transition-all shadow-md"
                  >
                    Crea il Tuo Liquore
                  </Link>
                </div>
              </div>
            </aside>

            {/* Gallery Grid */}
            <div className="flex-1">
          {filteredLiquors.length === 0 ? (
            <div className="text-center py-16">
              <Droplet className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Nessun liquore trovato</h3>
              <p className="text-gray-600">Prova a selezionare un altro filtro</p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredLiquors.map((liquor) => (
              <div 
                key={liquor.id} 
                className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1"
              >
                {/* Image - Forced Square (1:1 aspect ratio) */}
                <div className="relative aspect-square overflow-hidden group/image">
                  <img 
                    src={liquor.image} 
                    alt={liquor.name}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover/image:scale-110 cursor-pointer"
                    onClick={() => setZoomedLiquor(liquor)}
                  />
                  
                  {/* Zoom Button */}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setZoomedLiquor(liquor);
                    }}
                    className="absolute top-4 left-4 bg-white/90 hover:bg-white p-2 rounded-full shadow-lg transition-all opacity-0 group-hover/image:opacity-100"
                    title="Ingrandisci immagine"
                  >
                    <ZoomIn className="w-5 h-5 text-gray-700" />
                  </button>
                  
                  <div className="absolute top-4 right-4 bg-white/95 backdrop-blur-sm px-3 py-1 rounded-full">
                    <span className="text-sm font-semibold text-purple-600">{liquor.type}</span>
                  </div>
                  <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-4">
                    <h3 className="text-xl font-bold text-white">{liquor.name}</h3>
                  </div>
                </div>

                {/* Content */}
                <div className="p-6">
                  <p className="text-gray-600 mb-4 text-sm leading-relaxed">
                    {liquor.description}
                  </p>

                  {/* Botanicals */}
                  <div className="mb-4">
                    <div className="flex items-center mb-2">
                      <Leaf className="w-4 h-4 text-green-600 mr-2" />
                      <span className="text-xs font-semibold text-gray-700 uppercase tracking-wide">Botaniche</span>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {liquor.botanicals.map((botanical, index) => (
                        <span 
                          key={index}
                          className="text-xs bg-green-50 text-green-700 px-2 py-1 rounded-full border border-green-200"
                        >
                          {botanical}
                        </span>
                      ))}
                    </div>
                  </div>

                  {/* Client Info */}
                  <div className="border-t pt-4 mb-4">
                    <div className="flex items-center justify-between text-sm">
                      <div>
                        <p className="text-gray-500 text-xs">Realizzato per</p>
                        <p className="font-semibold text-gray-900">{liquor.client}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-gray-500 text-xs">Quantità</p>
                        <p className="font-semibold text-purple-600">{liquor.bottles} btg</p>
                      </div>
                    </div>
                  </div>

                  {/* CTA */}
                  <Link
                    to="/builder"
                    className="w-full flex items-center justify-center bg-gradient-to-r from-purple-600 to-purple-700 text-white px-4 py-3 rounded-lg font-semibold hover:from-purple-700 hover:to-purple-800 transition-all duration-300 group"
                  >
                    Crea il Tuo Liquore
                    <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
                  </Link>
                </div>
              </div>
            ))}
              </div>
            )}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-gradient-to-r from-purple-600 to-purple-800 text-white py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Droplet className="w-16 h-16 mx-auto mb-6 text-amber-300" />
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Vuoi Creare il Tuo Liquore Personalizzato?
          </h2>
          <p className="text-xl text-white/90 mb-8">
            Ogni bottiglia è una creazione unica. Inizia ora a personalizzare il tuo liquore artigianale.
          </p>
          <Link
            to="/builder"
            className="inline-flex items-center bg-white text-purple-600 px-8 py-4 rounded-lg font-bold text-lg hover:bg-amber-50 transition-all duration-300 transform hover:scale-105 shadow-xl"
          >
            Inizia la Personalizzazione
            <ArrowRight className="w-5 h-5 ml-2" />
          </Link>
        </div>
      </section>

      {/* Zoom Modal */}
      {zoomedLiquor && (
        <div 
          className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4"
          onClick={() => setZoomedLiquor(null)}
        >
          <div className="relative max-w-6xl w-full">
            {/* Image */}
            <img 
              src={zoomedLiquor.image} 
              alt={zoomedLiquor.name}
              className="max-w-full max-h-[80vh] object-contain mx-auto rounded-lg shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            />
            
            {/* Info Card */}
            <div 
              className="mt-6 bg-white/95 backdrop-blur-sm rounded-lg p-6 max-w-2xl mx-auto shadow-xl"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-start justify-between mb-4">
                <div>
                  <div className="inline-flex items-center bg-purple-100 px-3 py-1 rounded-full mb-2">
                    <span className="text-sm font-semibold text-purple-600">{zoomedLiquor.type}</span>
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900">{zoomedLiquor.name}</h3>
                </div>
              </div>
              
              <p className="text-gray-600 mb-4">{zoomedLiquor.description}</p>
              
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <p className="text-sm text-gray-500 mb-1">Realizzato per</p>
                  <p className="font-semibold text-gray-900">{zoomedLiquor.client}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500 mb-1">Quantità</p>
                  <p className="font-semibold text-purple-600">{zoomedLiquor.bottles} bottiglie</p>
                </div>
              </div>
              
              <div>
                <div className="flex items-center mb-2">
                  <Leaf className="w-4 h-4 text-green-600 mr-2" />
                  <span className="text-sm font-semibold text-gray-700 uppercase tracking-wide">Botaniche</span>
                </div>
                <div className="flex flex-wrap gap-2">
                  {zoomedLiquor.botanicals.map((botanical, index) => (
                    <span 
                      key={index}
                      className="text-sm bg-green-50 text-green-700 px-3 py-1 rounded-full border border-green-200"
                    >
                      {botanical}
                    </span>
                  ))}
                </div>
              </div>
            </div>
            
            {/* Close Button */}
            <button
              onClick={() => setZoomedLiquor(null)}
              className="absolute top-4 right-4 bg-white/90 hover:bg-white p-3 rounded-full shadow-lg transition-all"
            >
              <svg className="w-6 h-6 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default Gallery;
